import type { PaymentMode } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing Funnels plugin API (studio `FunnelsOwnerController`).
// `appId` is the CHAT id. Money is integer minor units in the funnel's currency.

export type FunnelStatus = 'draft' | 'active' | 'archived';

export type FunnelRecurring = { interval: 'day' | 'week' | 'month' | 'year'; intervalCount?: number };

export interface Funnel {
  id: string;
  appId: string;
  slug: string;
  name: string;
  currency: string;
  status: FunnelStatus;
  createdAt: string;
  updatedAt: string;
}

export interface FunnelOffer {
  id: string;
  funnelId: string;
  title: string;
  description: string | null;
  amount: number;
  compareAtAmount: number | null;
  recurring: FunnelRecurring | null;
  shopVariantId: string | null;
}

export interface FunnelOfferInput {
  title: string;
  description?: string | null;
  amount: number;
  compareAtAmount?: number | null;
  recurring?: FunnelRecurring | null;
}

export interface FunnelStep {
  id: string;
  funnelId: string;
  position: number;
  type: string;
  path: string;
  offerId: string | null;
  onAccept: string | null;
  onDecline: string | null;
  config: Record<string, unknown> | null;
}

/**
 * A step as written to PUT steps. `key` names the step so onAccept/onDecline
 * can point at it; an existing step id as the key keeps that step (and its stats).
 */
export interface FunnelStepInput {
  key?: string;
  type: string;
  path: string;
  offerId?: string | null;
  onAccept?: string | null;
  onDecline?: string | null;
  config?: Record<string, unknown> | null;
}

export interface FunnelDetail {
  funnel: Funnel;
  steps: FunnelStep[];
  offers: FunnelOffer[];
}

export interface FunnelStepStats {
  stepId: string;
  position: number;
  type: string;
  path: string;
  views: number;
  purchases: number;
  revenue: number;
  /** 0..1, rounded to 4 decimals. */
  conversion: number;
  /** Checkout only: offer id → paid checkouts that took the bump. */
  bumpTakes?: Record<string, number>;
}

export interface FunnelStats {
  funnelId: string;
  mode: PaymentMode;
  steps: FunnelStepStats[];
  revenue: number;
}

const base = (appId: string) => `/api/plugins/funnels/apps/${encodeURIComponent(appId)}`;
const seg = encodeURIComponent;

export async function apiListFunnels(appId: string): Promise<Funnel[]> {
  const { data } = await httpClient.get<Funnel[]>(base(appId));
  return data;
}

export async function apiCreateFunnel(
  appId: string,
  input: { name: string; slug?: string; currency: string },
): Promise<FunnelDetail> {
  const { data } = await httpClient.post<FunnelDetail>(base(appId), input);
  return data;
}

export async function apiGetFunnel(appId: string, funnelId: string): Promise<FunnelDetail> {
  const { data } = await httpClient.get<FunnelDetail>(`${base(appId)}/${seg(funnelId)}`);
  return data;
}

export async function apiUpdateFunnel(
  appId: string,
  funnelId: string,
  patch: { name?: string; slug?: string; status?: FunnelStatus },
): Promise<Funnel> {
  const { data } = await httpClient.patch<Funnel>(`${base(appId)}/${seg(funnelId)}`, patch);
  return data;
}

export async function apiSetFunnelSteps(
  appId: string,
  funnelId: string,
  steps: FunnelStepInput[],
): Promise<FunnelDetail> {
  const { data } = await httpClient.put<FunnelDetail>(`${base(appId)}/${seg(funnelId)}/steps`, { steps });
  return data;
}

export async function apiCreateFunnelOffer(
  appId: string,
  funnelId: string,
  offer: FunnelOfferInput,
): Promise<FunnelOffer> {
  const { data } = await httpClient.post<FunnelOffer>(`${base(appId)}/${seg(funnelId)}/offers`, offer);
  return data;
}

export async function apiUpdateFunnelOffer(
  appId: string,
  offerId: string,
  patch: Partial<FunnelOfferInput>,
): Promise<FunnelOffer> {
  const { data } = await httpClient.patch<FunnelOffer>(`${base(appId)}/offers/${seg(offerId)}`, patch);
  return data;
}

/** Refused (409) while a step still uses the offer. */
export async function apiDeleteFunnelOffer(appId: string, offerId: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/offers/${seg(offerId)}`);
}

export async function apiGetFunnelStats(appId: string, funnelId: string, mode: PaymentMode): Promise<FunnelStats> {
  const { data } = await httpClient.get<FunnelStats>(`${base(appId)}/${seg(funnelId)}/stats?mode=${mode}`);
  return data;
}
